import { useEffect, useState } from "react";
import axios from "axios";
import { Activity, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL_STYLES = {
  NYTT: "bg-pine/10 text-pine border-pine/20",
  VIKTIGT: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function BygdepulsBlock({ belowRubrikSlot = null }) {
  const [items, setItems] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bygdepuls-news`, { timeout: 12000 });
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setGeneratedAt(data?.generated_at || null);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // No filler content: Bygdepuls now means factual local news only.
  if (!loading && items.length === 0) return null;

  return (
    <section
      data-testid="bygdepuls-section"
      className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10"
    >
      <div className="bg-parchment border border-warm-border rounded-sm p-5 sm:p-6">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-pine" />
            <h2
              data-testid="bygdepuls-title"
              className="font-heading text-lg sm:text-xl font-semibold text-ink tracking-tight"
            >
              Bygdepuls
            </h2>
          </div>
          {!loading && items.length > 0 && (
            <span className="text-[10px] uppercase tracking-[0.2em] text-bark/40 font-body">
              {items.length} {items.length === 1 ? "notis" : "notiser"}
            </span>
          )}
        </div>
        <p className="text-xs sm:text-sm text-bark/60 mb-4 leading-relaxed">
          Det som faktiskt har hänt eller förändrats i Backe, Rossön och bygden.
          {generatedAt && (() => {
            try {
              const formatted = new Date(generatedAt).toLocaleString("sv-SE", {
                timeZone: "Europe/Stockholm",
                day: "numeric",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
              });
              return (
                <span data-testid="bygdepuls-updated" className="block sm:inline sm:ml-2 text-[11px] text-bark/40 mt-1 sm:mt-0">
                  · Uppdaterad {formatted}
                </span>
              );
            } catch {
              return null;
            }
          })()}
        </p>

        {belowRubrikSlot}

        {loading && (
          <p data-testid="bygdepuls-loading" className="text-xs text-muted-foreground py-2">
            Laddar…
          </p>
        )}

        {error && (
          <p data-testid="bygdepuls-error" className="text-xs text-falu/80 py-2">
            Kunde inte hämta puls just nu.
          </p>
        )}

        {!loading && items.length > 0 && (
          <ul data-testid="bygdepuls-list" className="flex flex-col divide-y divide-warm-border/60">
            {items.map((it, idx) => (
              <li
                key={`${it.source || "bygdepuls"}-${idx}-${it.title}`}
                data-testid={`bygdepuls-item-${idx}`}
                className="py-2.5 first:pt-0 last:pb-0"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`shrink-0 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                      LABEL_STYLES[it.label] || LABEL_STYLES.NYTT
                    }`}
                  >
                    {it.label || "NYTT"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-sm sm:text-base font-semibold text-ink leading-snug">
                      {it.title}
                    </p>
                    {it.subtitle && (
                      <p className="text-xs sm:text-sm text-bark/70 leading-snug mt-0.5">
                        {it.subtitle}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                      {it.meta && (
                        <p className="text-[10px] uppercase tracking-wider text-bark/40">
                          {it.meta}
                        </p>
                      )}
                      {it.source_url && (
                        <a
                          href={it.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`bygdepuls-source-${idx}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-falu hover:text-falu-hover no-underline"
                        >
                          Källa <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
